import React from 'react';

import { HStack } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { A11yWrapper } from 'components/a11y-wrapper';
import Currency from 'components/currency';
import { CurrencyLineSkeleton } from 'pages/cart/currency-line-skeleton';
import { primitive } from 'styles/constants/primitives';

import { Total, TotalLabel, TotalsRow } from './styled';
import { IRenderTotalsProps } from './types';
import { getFeesByGroup, getStandardizedName } from './utils';

const RewardRow = HStack.withConfig({
  _text: {
    color: Styles.color.validated,
  },
});

interface Cart {
  subTotalCents?: number;
  fees?: Array<any>;
}

export const SubTotal: React.FC<React.PropsWithChildren<IRenderTotalsProps>> = ({
  cart = {} as Cart,
  otherDiscountAmount,
  rewardDiscount,
  isLoading,
}) => {
  const { formatMessage } = useIntl();
  const { subTotalCents = 0, fees = [] } = cart;
  const itemFees = getFeesByGroup(fees || []);

  return (
    <>
      {!!rewardDiscount &&
        (isLoading ? (
          <CurrencyLineSkeleton />
        ) : (
          <RewardRow>
            <A11yWrapper>
              <TotalLabel marginBottom="$3" color={primitive.bk.$crunchyGreen}>
                {formatMessage({
                  id: 'rewardSavings',
                })}
              </TotalLabel>
            </A11yWrapper>
            <Total testID="reward-savings">
              <Currency
                prefix="- "
                amount={rewardDiscount}
                textProps={{ color: primitive.bk.$crunchyGreen, fontSize: 'md' }}
              />
            </Total>
          </RewardRow>
        ))}
      {!!otherDiscountAmount &&
        (isLoading ? (
          <CurrencyLineSkeleton />
        ) : (
          <RewardRow>
            <A11yWrapper>
              <TotalLabel>
                {formatMessage({
                  id: 'comboOfferSavings',
                })}
              </TotalLabel>
            </A11yWrapper>
            <Total testID="discount-savings">
              <Currency prefix="- " textProps={{ fontSize: 'md' }} amount={otherDiscountAmount} />
            </Total>
          </RewardRow>
        ))}

      {isLoading ? (
        <CurrencyLineSkeleton />
      ) : (
        <TotalsRow>
          <A11yWrapper>
            <TotalLabel>
              {formatMessage({
                id: 'subtotal',
              })}
            </TotalLabel>
          </A11yWrapper>
          <Total testID="subtotal">
            <Currency textProps={{ fontSize: 'md' }} amount={Math.max(subTotalCents, 0)} />
          </Total>
        </TotalsRow>
      )}
      {!!itemFees &&
        Object.entries(itemFees).map(([name, entryCents]: [string, number], index) =>
          isLoading ? (
            <CurrencyLineSkeleton />
          ) : (
            <TotalsRow key={index}>
              <A11yWrapper>
                <TotalLabel testID={`fee-name-${name}`}>
                  {formatMessage({ id: `${getStandardizedName({ name })}Fee` })}
                </TotalLabel>
              </A11yWrapper>
              <Total testID={`fee-amount-${name}`}>
                <Currency textProps={{ fontSize: 'md' }} amount={entryCents} />
              </Total>
            </TotalsRow>
          )
        )}
    </>
  );
};
